// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { type ParseKeys } from 'i18next';

export type OnboardingQuestionTestType = 'suitability' | 'appropriateness';

export interface OnboardingQuestionOption {
  readonly id: string;
  readonly value: string;
  readonly label: [ParseKeys<'components'>, { ns: 'components' }];
  readonly score: Readonly<Record<OnboardingQuestionTestType, number>>;
  readonly disabled?: boolean;
}

export interface OnboardingQuestion {
  readonly id: OnboardingQuestionId;
  readonly block: OnboardingQuestionBlock;
  readonly label: [ParseKeys<'components'>, { ns: 'components' }];
  readonly types: OnboardingQuestionTestType[];
  readonly options: OnboardingQuestionOption[];
}

export type OnboardingQuestionId =
  | 'q_investment_horizon'
  | 'q_risk_tolerance'
  | 'q_investment_purpose'
  | 'q_loss_reaction'
  | 'q_education_level'
  | 'q_financial_experience'
  | 'q_financial_knowledge'
  | 'q_stocks_knowledge'
  | 'q_fund_rating_knowledge'
  | 'q_pe_funds_knowledge'
  | 'q_etf_knowledge'
  | 'q_credit_risk_knowledge'
  | 'q_liquidity_risk_knowledge'
  | 'q_market_risk_knowledge'
  | 'q_currency_risk_knowledge'
  | 'q_exp_gov_bonds'
  | 'q_exp_listed_equity'
  | 'q_exp_mutual_funds'
  | 'q_exp_etf_simple'
  | 'q_exp_unlisted_equity'
  | 'q_exp_structured_guaranteed'
  | 'q_exp_structured_unguaranteed'
  | 'q_exp_complex_bonds'
  | 'q_exp_hybrid_products'
  | 'q_exp_alternative_invest'
  | 'q_exp_exchange_derivatives'
  | 'q_exp_otc_derivatives'
  | 'q_income_level'
  | 'q_salary_percentage'
  | 'q_payment_obligations'
  | 'q_liquidity_needs'
  | 'q_liquid_assets_ratio'
  | 'q_sustainability_preferences'
  | 'q_sustainability_percentage'
  | 'q_sustainability_criteria'
  | 'q_taxonomy_percentage'
  | 'q_sfdr_percentage'
  | 'q_pias_preference';

export enum OnboardingQuestionBlock {
  ContractReview = 'contract-review',
  InvestmentObjectives = 'investment-objectives',
  FinancialKnowledge = 'financial-knowledge',
  InvestmentExperience = 'investment-experience',
  FinancialSituation = 'financial-situation',
  Sustainability = 'sustainability',
}

export type OnboardingQuestions = Record<
  OnboardingQuestionBlock,
  OnboardingQuestion[]
>;

const INVESTMENT_OBJECTIVES_QUESTIONS: OnboardingQuestion[] = [
  {
    id: 'q_investment_horizon',
    block: OnboardingQuestionBlock.InvestmentObjectives,
    label: [
      'onboarding-questions.investment-objectives.q_investment_horizon.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'lt_1y',
        value: 'lt_1y',
        label: [
          'onboarding-questions.investment-objectives.q_investment_horizon.options.lt_1y',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: '1_3y',
        value: '1_3y',
        label: [
          'onboarding-questions.investment-objectives.q_investment_horizon.options.1_3y',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 0 },
      },
      {
        id: '3_5y',
        value: '3_5y',
        label: [
          'onboarding-questions.investment-objectives.q_investment_horizon.options.3_5y',
          { ns: 'components' },
        ],
        score: { suitability: 3, appropriateness: 0 },
      },
      {
        id: 'gt_5y',
        value: 'gt_5y',
        label: [
          'onboarding-questions.investment-objectives.q_investment_horizon.options.gt_5y',
          { ns: 'components' },
        ],
        score: { suitability: 4, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_risk_tolerance',
    block: OnboardingQuestionBlock.InvestmentObjectives,
    label: [
      'onboarding-questions.investment-objectives.q_risk_tolerance.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'no_risk_capital',
        value: 'no_risk_capital',
        label: [
          'onboarding-questions.investment-objectives.q_risk_tolerance.options.no_risk_capital',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'small_temp_loss',
        value: 'small_temp_loss',
        label: [
          'onboarding-questions.investment-objectives.q_risk_tolerance.options.small_temp_loss',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 0 },
      },
      {
        id: 'mod_risk_return',
        value: 'mod_risk_return',
        label: [
          'onboarding-questions.investment-objectives.q_risk_tolerance.options.mod_risk_return',
          { ns: 'components' },
        ],
        score: { suitability: 3, appropriateness: 0 },
      },
      {
        id: 'high_risk_high_return',
        value: 'high_risk_high_return',
        label: [
          'onboarding-questions.investment-objectives.q_risk_tolerance.options.high_risk_high_return',
          { ns: 'components' },
        ],
        score: { suitability: 4, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_investment_purpose',
    block: OnboardingQuestionBlock.InvestmentObjectives,
    label: [
      'onboarding-questions.investment-objectives.q_investment_purpose.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'capital_preservation',
        value: 'capital_preservation',
        label: [
          'onboarding-questions.investment-objectives.q_investment_purpose.options.capital_preservation',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'limited_growth',
        value: 'limited_growth',
        label: [
          'onboarding-questions.investment-objectives.q_investment_purpose.options.limited_growth',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 0 },
      },
      {
        id: 'moderate_growth',
        value: 'moderate_growth',
        label: [
          'onboarding-questions.investment-objectives.q_investment_purpose.options.moderate_growth',
          { ns: 'components' },
        ],
        score: { suitability: 3, appropriateness: 0 },
      },
      {
        id: 'high_growth_high_risk',
        value: 'high_growth_high_risk',
        label: [
          'onboarding-questions.investment-objectives.q_investment_purpose.options.high_growth_high_risk',
          { ns: 'components' },
        ],
        score: { suitability: 4, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_loss_reaction',
    block: OnboardingQuestionBlock.InvestmentObjectives,
    label: [
      'onboarding-questions.investment-objectives.q_loss_reaction.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'avoid_volatile_assets',
        value: 'avoid_volatile_assets',
        label: [
          'onboarding-questions.investment-objectives.q_loss_reaction.options.avoid_volatile_assets',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'sell_all_immediately',
        value: 'sell_all_immediately',
        label: [
          'onboarding-questions.investment-objectives.q_loss_reaction.options.sell_all_immediately',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 0 },
      },
      {
        id: 'sell_partial',
        value: 'sell_partial',
        label: [
          'onboarding-questions.investment-objectives.q_loss_reaction.options.sell_partial',
          { ns: 'components' },
        ],
        score: { suitability: 3, appropriateness: 0 },
      },
      {
        id: 'hold_position',
        value: 'hold_position',
        label: [
          'onboarding-questions.investment-objectives.q_loss_reaction.options.hold_position',
          { ns: 'components' },
        ],
        score: { suitability: 4, appropriateness: 0 },
      },
      {
        id: 'increase_investment',
        value: 'increase_investment',
        label: [
          'onboarding-questions.investment-objectives.q_loss_reaction.options.increase_investment',
          { ns: 'components' },
        ],
        score: { suitability: 5, appropriateness: 0 },
      },
    ],
  },
];

const FINANCIAL_KNOWLEDGE_QUESTIONS: OnboardingQuestion[] = [
  {
    id: 'q_education_level',
    block: OnboardingQuestionBlock.FinancialKnowledge,
    label: [
      'onboarding-questions.financial-knowledge.q_education_level.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'secondary_education',
        value: 'secondary_education',
        label: [
          'onboarding-questions.financial-knowledge.q_education_level.options.secondary_education',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 0 },
      },
      {
        id: 'higher_education',
        value: 'higher_education',
        label: [
          'onboarding-questions.financial-knowledge.q_education_level.options.higher_education',
          { ns: 'components' },
        ],
        score: { suitability: 3, appropriateness: 16 },
      },
      {
        id: 'finance_education',
        value: 'finance_education',
        label: [
          'onboarding-questions.financial-knowledge.q_education_level.options.finance_education',
          { ns: 'components' },
        ],
        score: { suitability: 4, appropriateness: 16 },
      },
      {
        id: 'other_education',
        value: 'other_education',
        label: [
          'onboarding-questions.financial-knowledge.q_education_level.options.other_education',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: -99 },
      },
    ],
  },
  {
    id: 'q_financial_experience',
    block: OnboardingQuestionBlock.FinancialKnowledge,
    label: [
      'onboarding-questions.financial-knowledge.q_financial_experience.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'no_finance_exp',
        value: 'no_finance_exp',
        label: [
          'onboarding-questions.financial-knowledge.q_financial_experience.options.no_finance_exp',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'occasional_finance_exp',
        value: 'occasional_finance_exp',
        label: [
          'onboarding-questions.financial-knowledge.q_financial_experience.options.occasional_finance_exp',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 0 },
      },
      {
        id: 'direct_finance_exp',
        value: 'direct_finance_exp',
        label: [
          'onboarding-questions.financial-knowledge.q_financial_experience.options.direct_finance_exp',
          { ns: 'components' },
        ],
        score: { suitability: 4, appropriateness: 16 },
      },
    ],
  },
  {
    id: 'q_financial_knowledge',
    block: OnboardingQuestionBlock.FinancialKnowledge,
    label: [
      'onboarding-questions.financial-knowledge.q_financial_knowledge.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'basic_knowledge',
        value: 'basic_knowledge',
        label: [
          'onboarding-questions.financial-knowledge.q_financial_knowledge.options.basic_knowledge',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: -99 },
      },
      {
        id: 'basic_concepts',
        value: 'basic_concepts',
        label: [
          'onboarding-questions.financial-knowledge.q_financial_knowledge.options.basic_concepts',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 9 },
      },
      {
        id: 'intermediate_concepts',
        value: 'intermediate_concepts',
        label: [
          'onboarding-questions.financial-knowledge.q_financial_knowledge.options.intermediate_concepts',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 16 },
      },
      {
        id: 'advanced_concepts',
        value: 'advanced_concepts',
        label: [
          'onboarding-questions.financial-knowledge.q_financial_knowledge.options.advanced_concepts',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 16 },
      },
    ],
  },
  {
    id: 'q_stocks_knowledge',
    block: OnboardingQuestionBlock.FinancialKnowledge,
    label: [
      'onboarding-questions.financial-knowledge.q_stocks_knowledge.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'value_not_market_price',
        value: 'value_not_market_price',
        label: [
          'onboarding-questions.financial-knowledge.q_stocks_knowledge.options.value_not_market_price',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'dividend_only_return',
        value: 'dividend_only_return',
        label: [
          'onboarding-questions.financial-knowledge.q_stocks_knowledge.options.dividend_only_return',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'secondary_market_trading',
        value: 'secondary_market_trading',
        label: [
          'onboarding-questions.financial-knowledge.q_stocks_knowledge.options.secondary_market_trading',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 16 },
      },
      {
        id: 'dont_know_stocks',
        value: 'dont_know_stocks',
        label: [
          'onboarding-questions.financial-knowledge.q_stocks_knowledge.options.dont_know_stocks',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_fund_rating_knowledge',
    block: OnboardingQuestionBlock.FinancialKnowledge,
    label: [
      'onboarding-questions.financial-knowledge.q_fund_rating_knowledge.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'fund_risk_level',
        value: 'fund_risk_level',
        label: [
          'onboarding-questions.financial-knowledge.q_fund_rating_knowledge.options.fund_risk_level',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 16 },
      },
      {
        id: 'fund_return_potential',
        value: 'fund_return_potential',
        label: [
          'onboarding-questions.financial-knowledge.q_fund_rating_knowledge.options.fund_return_potential',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'fund_knowledge_req',
        value: 'fund_knowledge_req',
        label: [
          'onboarding-questions.financial-knowledge.q_fund_rating_knowledge.options.fund_knowledge_req',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'dont_know_fund_rating',
        value: 'dont_know_fund_rating',
        label: [
          'onboarding-questions.financial-knowledge.q_fund_rating_knowledge.options.dont_know_fund_rating',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_pe_funds_knowledge',
    block: OnboardingQuestionBlock.FinancialKnowledge,
    label: [
      'onboarding-questions.financial-knowledge.q_pe_funds_knowledge.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'private_equity_invest',
        value: 'private_equity_invest',
        label: [
          'onboarding-questions.financial-knowledge.q_pe_funds_knowledge.options.private_equity_invest',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'highly_liquid_funds',
        value: 'highly_liquid_funds',
        label: [
          'onboarding-questions.financial-knowledge.q_pe_funds_knowledge.options.highly_liquid_funds',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 1 },
      },
      {
        id: 'min_investment_amount',
        value: 'min_investment_amount',
        label: [
          'onboarding-questions.financial-knowledge.q_pe_funds_knowledge.options.min_investment_amount',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'dont_know_pe_funds',
        value: 'dont_know_pe_funds',
        label: [
          'onboarding-questions.financial-knowledge.q_pe_funds_knowledge.options.dont_know_pe_funds',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_etf_knowledge',
    block: OnboardingQuestionBlock.FinancialKnowledge,
    label: [
      'onboarding-questions.financial-knowledge.q_etf_knowledge.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'index_replication',
        value: 'index_replication',
        label: [
          'onboarding-questions.financial-knowledge.q_etf_knowledge.options.index_replication',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 1 },
      },
      {
        id: 'not_collective_invest',
        value: 'not_collective_invest',
        label: [
          'onboarding-questions.financial-knowledge.q_etf_knowledge.options.not_collective_invest',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'no_loss_diversification',
        value: 'no_loss_diversification',
        label: [
          'onboarding-questions.financial-knowledge.q_etf_knowledge.options.no_loss_diversification',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'dont_know_etf',
        value: 'dont_know_etf',
        label: [
          'onboarding-questions.financial-knowledge.q_etf_knowledge.options.dont_know_etf',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_credit_risk_knowledge',
    block: OnboardingQuestionBlock.FinancialKnowledge,
    label: [
      'onboarding-questions.financial-knowledge.q_credit_risk_knowledge.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'dont_know_credit_risk',
        value: 'dont_know_credit_risk',
        label: [
          'onboarding-questions.financial-knowledge.q_credit_risk_knowledge.options.dont_know_credit_risk',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'default_payment_risk',
        value: 'default_payment_risk',
        label: [
          'onboarding-questions.financial-knowledge.q_credit_risk_knowledge.options.default_payment_risk',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 1 },
      },
      {
        id: 'currency_price_risk',
        value: 'currency_price_risk',
        label: [
          'onboarding-questions.financial-knowledge.q_credit_risk_knowledge.options.currency_price_risk',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_liquidity_risk_knowledge',
    block: OnboardingQuestionBlock.FinancialKnowledge,
    label: [
      'onboarding-questions.financial-knowledge.q_liquidity_risk_knowledge.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'dont_know_liquidity_risk',
        value: 'dont_know_liquidity_risk',
        label: [
          'onboarding-questions.financial-knowledge.q_liquidity_risk_knowledge.options.dont_know_liquidity_risk',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'access_limitation_risk',
        value: 'access_limitation_risk',
        label: [
          'onboarding-questions.financial-knowledge.q_liquidity_risk_knowledge.options.access_limitation_risk',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 1 },
      },
      {
        id: 'currency_value_risk',
        value: 'currency_value_risk',
        label: [
          'onboarding-questions.financial-knowledge.q_liquidity_risk_knowledge.options.currency_value_risk',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_market_risk_knowledge',
    block: OnboardingQuestionBlock.FinancialKnowledge,
    label: [
      'onboarding-questions.financial-knowledge.q_market_risk_knowledge.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'dont_know_market_risk',
        value: 'dont_know_market_risk',
        label: [
          'onboarding-questions.financial-knowledge.q_market_risk_knowledge.options.dont_know_market_risk',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'asset_price_volatility',
        value: 'asset_price_volatility',
        label: [
          'onboarding-questions.financial-knowledge.q_market_risk_knowledge.options.asset_price_volatility',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 1 },
      },
      {
        id: 'interest_rate_fluctuation',
        value: 'interest_rate_fluctuation',
        label: [
          'onboarding-questions.financial-knowledge.q_market_risk_knowledge.options.interest_rate_fluctuation',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_currency_risk_knowledge',
    block: OnboardingQuestionBlock.FinancialKnowledge,
    label: [
      'onboarding-questions.financial-knowledge.q_currency_risk_knowledge.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'dont_know_currency_risk',
        value: 'dont_know_currency_risk',
        label: [
          'onboarding-questions.financial-knowledge.q_currency_risk_knowledge.options.dont_know_currency_risk',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'exchange_rate_variation',
        value: 'exchange_rate_variation',
        label: [
          'onboarding-questions.financial-knowledge.q_currency_risk_knowledge.options.exchange_rate_variation',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 1 },
      },
      {
        id: 'volatility_fluctuation',
        value: 'volatility_fluctuation',
        label: [
          'onboarding-questions.financial-knowledge.q_currency_risk_knowledge.options.volatility_fluctuation',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
];

const INVESTMENT_EXPERIENCE_QUESTIONS: OnboardingQuestion[] = [
  {
    id: 'q_exp_gov_bonds',
    block: OnboardingQuestionBlock.InvestmentExperience,
    label: [
      'onboarding-questions.investment-experience.q_exp_gov_bonds.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'gov_bonds_2plus',
        value: 'gov_bonds_2plus',
        label: [
          'onboarding-questions.investment-experience.q_exp_gov_bonds.options.gov_bonds_2plus',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 1 },
      },
      {
        id: 'gov_bonds_1',
        value: 'gov_bonds_1',
        label: [
          'onboarding-questions.investment-experience.q_exp_gov_bonds.options.gov_bonds_1',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'gov_bonds_none',
        value: 'gov_bonds_none',
        label: [
          'onboarding-questions.investment-experience.q_exp_gov_bonds.options.gov_bonds_none',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_exp_listed_equity',
    block: OnboardingQuestionBlock.InvestmentExperience,
    label: [
      'onboarding-questions.investment-experience.q_exp_listed_equity.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'listed_equity_2plus',
        value: 'listed_equity_2plus',
        label: [
          'onboarding-questions.investment-experience.q_exp_listed_equity.options.listed_equity_2plus',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 1 },
      },
      {
        id: 'listed_equity_1',
        value: 'listed_equity_1',
        label: [
          'onboarding-questions.investment-experience.q_exp_listed_equity.options.listed_equity_1',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'listed_equity_none',
        value: 'listed_equity_none',
        label: [
          'onboarding-questions.investment-experience.q_exp_listed_equity.options.listed_equity_none',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_exp_mutual_funds',
    block: OnboardingQuestionBlock.InvestmentExperience,
    label: [
      'onboarding-questions.investment-experience.q_exp_mutual_funds.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'mutual_funds_2plus',
        value: 'mutual_funds_2plus',
        label: [
          'onboarding-questions.investment-experience.q_exp_mutual_funds.options.mutual_funds_2plus',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 1 },
      },
      {
        id: 'mutual_funds_1',
        value: 'mutual_funds_1',
        label: [
          'onboarding-questions.investment-experience.q_exp_mutual_funds.options.mutual_funds_1',
          { ns: 'components' },
        ],
        score: { suitability: 0.5, appropriateness: 0 },
      },
      {
        id: 'mutual_funds_none',
        value: 'mutual_funds_none',
        label: [
          'onboarding-questions.investment-experience.q_exp_mutual_funds.options.mutual_funds_none',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_exp_etf_simple',
    block: OnboardingQuestionBlock.InvestmentExperience,
    label: [
      'onboarding-questions.investment-experience.q_exp_etf_simple.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'etf_simple_2plus',
        value: 'etf_simple_2plus',
        label: [
          'onboarding-questions.investment-experience.q_exp_etf_simple.options.etf_simple_2plus',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 2 },
      },
      {
        id: 'etf_simple_1',
        value: 'etf_simple_1',
        label: [
          'onboarding-questions.investment-experience.q_exp_etf_simple.options.etf_simple_1',
          { ns: 'components' },
        ],
        score: { suitability: 0.5, appropriateness: 0 },
      },
      {
        id: 'etf_simple_none',
        value: 'etf_simple_none',
        label: [
          'onboarding-questions.investment-experience.q_exp_etf_simple.options.etf_simple_none',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_exp_unlisted_equity',
    block: OnboardingQuestionBlock.InvestmentExperience,
    label: [
      'onboarding-questions.investment-experience.q_exp_unlisted_equity.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'unlisted_equity_2plus',
        value: 'unlisted_equity_2plus',
        label: [
          'onboarding-questions.investment-experience.q_exp_unlisted_equity.options.unlisted_equity_2plus',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 1 },
      },
      {
        id: 'unlisted_equity_1',
        value: 'unlisted_equity_1',
        label: [
          'onboarding-questions.investment-experience.q_exp_unlisted_equity.options.unlisted_equity_1',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'unlisted_equity_none',
        value: 'unlisted_equity_none',
        label: [
          'onboarding-questions.investment-experience.q_exp_unlisted_equity.options.unlisted_equity_none',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_exp_structured_guaranteed',
    block: OnboardingQuestionBlock.InvestmentExperience,
    label: [
      'onboarding-questions.investment-experience.q_exp_structured_guaranteed.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'structured_guaranteed_2plus',
        value: 'structured_guaranteed_2plus',
        label: [
          'onboarding-questions.investment-experience.q_exp_structured_guaranteed.options.structured_guaranteed_2plus',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 1 },
      },
      {
        id: 'structured_guaranteed_1',
        value: 'structured_guaranteed_1',
        label: [
          'onboarding-questions.investment-experience.q_exp_structured_guaranteed.options.structured_guaranteed_1',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'structured_guaranteed_none',
        value: 'structured_guaranteed_none',
        label: [
          'onboarding-questions.investment-experience.q_exp_structured_guaranteed.options.structured_guaranteed_none',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_exp_structured_unguaranteed',
    block: OnboardingQuestionBlock.InvestmentExperience,
    label: [
      'onboarding-questions.investment-experience.q_exp_structured_unguaranteed.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'structured_unguaranteed_2plus',
        value: 'structured_unguaranteed_2plus',
        label: [
          'onboarding-questions.investment-experience.q_exp_structured_unguaranteed.options.structured_unguaranteed_2plus',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 1 },
      },
      {
        id: 'structured_unguaranteed_1',
        value: 'structured_unguaranteed_1',
        label: [
          'onboarding-questions.investment-experience.q_exp_structured_unguaranteed.options.structured_unguaranteed_1',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'structured_unguaranteed_none',
        value: 'structured_unguaranteed_none',
        label: [
          'onboarding-questions.investment-experience.q_exp_structured_unguaranteed.options.structured_unguaranteed_none',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_exp_complex_bonds',
    block: OnboardingQuestionBlock.InvestmentExperience,
    label: [
      'onboarding-questions.investment-experience.q_exp_complex_bonds.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'complex_bonds_2plus',
        value: 'complex_bonds_2plus',
        label: [
          'onboarding-questions.investment-experience.q_exp_complex_bonds.options.complex_bonds_2plus',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 1 },
      },
      {
        id: 'complex_bonds_1',
        value: 'complex_bonds_1',
        label: [
          'onboarding-questions.investment-experience.q_exp_complex_bonds.options.complex_bonds_1',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'complex_bonds_none',
        value: 'complex_bonds_none',
        label: [
          'onboarding-questions.investment-experience.q_exp_complex_bonds.options.complex_bonds_none',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_exp_hybrid_products',
    block: OnboardingQuestionBlock.InvestmentExperience,
    label: [
      'onboarding-questions.investment-experience.q_exp_hybrid_products.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'hybrid_products_2plus',
        value: 'hybrid_products_2plus',
        label: [
          'onboarding-questions.investment-experience.q_exp_hybrid_products.options.hybrid_products_2plus',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 1 },
      },
      {
        id: 'hybrid_products_1',
        value: 'hybrid_products_1',
        label: [
          'onboarding-questions.investment-experience.q_exp_hybrid_products.options.hybrid_products_1',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'hybrid_products_none',
        value: 'hybrid_products_none',
        label: [
          'onboarding-questions.investment-experience.q_exp_hybrid_products.options.hybrid_products_none',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_exp_alternative_invest',
    block: OnboardingQuestionBlock.InvestmentExperience,
    label: [
      'onboarding-questions.investment-experience.q_exp_alternative_invest.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'alternative_invest_2plus',
        value: 'alternative_invest_2plus',
        label: [
          'onboarding-questions.investment-experience.q_exp_alternative_invest.options.alternative_invest_2plus',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 2 },
      },
      {
        id: 'alternative_invest_1',
        value: 'alternative_invest_1',
        label: [
          'onboarding-questions.investment-experience.q_exp_alternative_invest.options.alternative_invest_1',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'alternative_invest_none',
        value: 'alternative_invest_none',
        label: [
          'onboarding-questions.investment-experience.q_exp_alternative_invest.options.alternative_invest_none',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_exp_exchange_derivatives',
    block: OnboardingQuestionBlock.InvestmentExperience,
    label: [
      'onboarding-questions.investment-experience.q_exp_exchange_derivatives.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'exchange_derivatives_2plus',
        value: 'exchange_derivatives_2plus',
        label: [
          'onboarding-questions.investment-experience.q_exp_exchange_derivatives.options.exchange_derivatives_2plus',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 1 },
      },
      {
        id: 'exchange_derivatives_1',
        value: 'exchange_derivatives_1',
        label: [
          'onboarding-questions.investment-experience.q_exp_exchange_derivatives.options.exchange_derivatives_1',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'exchange_derivatives_none',
        value: 'exchange_derivatives_none',
        label: [
          'onboarding-questions.investment-experience.q_exp_exchange_derivatives.options.exchange_derivatives_none',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_exp_otc_derivatives',
    block: OnboardingQuestionBlock.InvestmentExperience,
    label: [
      'onboarding-questions.investment-experience.q_exp_otc_derivatives.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'otc_derivatives_2plus',
        value: 'otc_derivatives_2plus',
        label: [
          'onboarding-questions.investment-experience.q_exp_otc_derivatives.options.otc_derivatives_2plus',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 1 },
      },
      {
        id: 'otc_derivatives_1',
        value: 'otc_derivatives_1',
        label: [
          'onboarding-questions.investment-experience.q_exp_otc_derivatives.options.otc_derivatives_1',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'otc_derivatives_none',
        value: 'otc_derivatives_none',
        label: [
          'onboarding-questions.investment-experience.q_exp_otc_derivatives.options.otc_derivatives_none',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
];

const FINANCIAL_SITUATION_QUESTIONS: OnboardingQuestion[] = [
  {
    id: 'q_income_level',
    block: OnboardingQuestionBlock.FinancialSituation,
    label: [
      'onboarding-questions.financial-situation.q_income_level.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'income_lt_20k',
        value: 'income_lt_20k',
        label: [
          'onboarding-questions.financial-situation.q_income_level.options.income_lt_20k',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'income_20k_60k',
        value: 'income_20k_60k',
        label: [
          'onboarding-questions.financial-situation.q_income_level.options.income_20k_60k',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 0 },
      },
      {
        id: 'income_60k_300k',
        value: 'income_60k_300k',
        label: [
          'onboarding-questions.financial-situation.q_income_level.options.income_60k_300k',
          { ns: 'components' },
        ],
        score: { suitability: 3, appropriateness: 0 },
      },
      {
        id: 'income_gt_300k',
        value: 'income_gt_300k',
        label: [
          'onboarding-questions.financial-situation.q_income_level.options.income_gt_300k',
          { ns: 'components' },
        ],
        score: { suitability: 4, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_salary_percentage',
    block: OnboardingQuestionBlock.FinancialSituation,
    label: [
      'onboarding-questions.financial-situation.q_salary_percentage.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'salary_pct_gt_75',
        value: 'salary_pct_gt_75',
        label: [
          'onboarding-questions.financial-situation.q_salary_percentage.options.salary_pct_gt_75',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'salary_pct_50_75',
        value: 'salary_pct_50_75',
        label: [
          'onboarding-questions.financial-situation.q_salary_percentage.options.salary_pct_50_75',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 0 },
      },
      {
        id: 'salary_pct_25_50',
        value: 'salary_pct_25_50',
        label: [
          'onboarding-questions.financial-situation.q_salary_percentage.options.salary_pct_25_50',
          { ns: 'components' },
        ],
        score: { suitability: 3, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_payment_obligations',
    block: OnboardingQuestionBlock.FinancialSituation,
    label: [
      'onboarding-questions.financial-situation.q_payment_obligations.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'obligations_gt_50',
        value: 'obligations_gt_50',
        label: [
          'onboarding-questions.financial-situation.q_payment_obligations.options.obligations_gt_50',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'obligations_35_50',
        value: 'obligations_35_50',
        label: [
          'onboarding-questions.financial-situation.q_payment_obligations.options.obligations_35_50',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 0 },
      },
      {
        id: 'obligations_10_35',
        value: 'obligations_10_35',
        label: [
          'onboarding-questions.financial-situation.q_payment_obligations.options.obligations_10_35',
          { ns: 'components' },
        ],
        score: { suitability: 3, appropriateness: 0 },
      },
      {
        id: 'obligations_lt_10',
        value: 'obligations_lt_10',
        label: [
          'onboarding-questions.financial-situation.q_payment_obligations.options.obligations_lt_10',
          { ns: 'components' },
        ],
        score: { suitability: 4, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_liquidity_needs',
    block: OnboardingQuestionBlock.FinancialSituation,
    label: [
      'onboarding-questions.financial-situation.q_liquidity_needs.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'need_funds_lt_6m',
        value: 'need_funds_lt_6m',
        label: [
          'onboarding-questions.financial-situation.q_liquidity_needs.options.need_funds_lt_6m',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'need_funds_6m_2y',
        value: 'need_funds_6m_2y',
        label: [
          'onboarding-questions.financial-situation.q_liquidity_needs.options.need_funds_6m_2y',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 0 },
      },
      {
        id: 'need_funds_2_5y',
        value: 'need_funds_2_5y',
        label: [
          'onboarding-questions.financial-situation.q_liquidity_needs.options.need_funds_2_5y',
          { ns: 'components' },
        ],
        score: { suitability: 3, appropriateness: 0 },
      },
      {
        id: 'no_liquidity_need',
        value: 'no_liquidity_need',
        label: [
          'onboarding-questions.financial-situation.q_liquidity_needs.options.no_liquidity_need',
          { ns: 'components' },
        ],
        score: { suitability: 4, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_liquid_assets_ratio',
    block: OnboardingQuestionBlock.FinancialSituation,
    label: [
      'onboarding-questions.financial-situation.q_liquid_assets_ratio.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'liquid_assets_lt_25',
        value: 'liquid_assets_lt_25',
        label: [
          'onboarding-questions.financial-situation.q_liquid_assets_ratio.options.liquid_assets_lt_25',
          { ns: 'components' },
        ],
        score: { suitability: 1, appropriateness: 0 },
      },
      {
        id: 'liquid_assets_25_50',
        value: 'liquid_assets_25_50',
        label: [
          'onboarding-questions.financial-situation.q_liquid_assets_ratio.options.liquid_assets_25_50',
          { ns: 'components' },
        ],
        score: { suitability: 2, appropriateness: 0 },
      },
      {
        id: 'liquid_assets_50_75',
        value: 'liquid_assets_50_75',
        label: [
          'onboarding-questions.financial-situation.q_liquid_assets_ratio.options.liquid_assets_50_75',
          { ns: 'components' },
        ],
        score: { suitability: 3, appropriateness: 0 },
      },
      {
        id: 'liquid_assets_gt_75',
        value: 'liquid_assets_gt_75',
        label: [
          'onboarding-questions.financial-situation.q_liquid_assets_ratio.options.liquid_assets_gt_75',
          { ns: 'components' },
        ],
        score: { suitability: 4, appropriateness: 0 },
      },
    ],
  },
];

const SUSTAINABILITY_QUESTIONS: OnboardingQuestion[] = [
  {
    id: 'q_sustainability_preferences',
    block: OnboardingQuestionBlock.Sustainability,
    label: [
      'onboarding-questions.sustainability.q_sustainability_preferences.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'no_sustainability_preferences',
        value: 'no_sustainability_preferences',
        label: [
          'onboarding-questions.sustainability.q_sustainability_preferences.options.no_sustainability_preferences',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'neutral_sustainability',
        value: 'neutral_sustainability',
        label: [
          'onboarding-questions.sustainability.q_sustainability_preferences.options.neutral_sustainability',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'delegate_sustainability',
        value: 'delegate_sustainability',
        label: [
          'onboarding-questions.sustainability.q_sustainability_preferences.options.delegate_sustainability',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'determine_sustainability_criteria',
        value: 'determine_sustainability_criteria',
        label: [
          'onboarding-questions.sustainability.q_sustainability_preferences.options.determine_sustainability_criteria',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
        disabled: true,
      },
    ],
  },
  {
    id: 'q_sustainability_percentage',
    block: OnboardingQuestionBlock.Sustainability,
    label: [
      'onboarding-questions.sustainability.q_sustainability_percentage.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'sustainability_60',
        value: 'sustainability_60',
        label: [
          'onboarding-questions.sustainability.q_sustainability_percentage.options.sustainability_60',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_sustainability_criteria',
    block: OnboardingQuestionBlock.Sustainability,
    label: [
      'onboarding-questions.sustainability.q_sustainability_criteria.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'eu_taxonomy',
        value: 'eu_taxonomy',
        label: [
          'onboarding-questions.sustainability.q_sustainability_criteria.options.eu_taxonomy',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'sfdr',
        value: 'sfdr',
        label: [
          'onboarding-questions.sustainability.q_sustainability_criteria.options.sfdr',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'pias',
        value: 'pias',
        label: [
          'onboarding-questions.sustainability.q_sustainability_criteria.options.pias',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_taxonomy_percentage',
    block: OnboardingQuestionBlock.Sustainability,
    label: [
      'onboarding-questions.sustainability.q_taxonomy_percentage.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'taxonomy_0',
        value: 'taxonomy_0',
        label: [
          'onboarding-questions.sustainability.q_taxonomy_percentage.options.taxonomy_0',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'taxonomy_5',
        value: 'taxonomy_5',
        label: [
          'onboarding-questions.sustainability.q_taxonomy_percentage.options.taxonomy_5',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'taxonomy_25',
        value: 'taxonomy_25',
        label: [
          'onboarding-questions.sustainability.q_taxonomy_percentage.options.taxonomy_25',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'taxonomy_50',
        value: 'taxonomy_50',
        label: [
          'onboarding-questions.sustainability.q_taxonomy_percentage.options.taxonomy_50',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_sfdr_percentage',
    block: OnboardingQuestionBlock.Sustainability,
    label: [
      'onboarding-questions.sustainability.q_sfdr_percentage.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'sfdr_0',
        value: 'sfdr_0',
        label: [
          'onboarding-questions.sustainability.q_sfdr_percentage.options.sfdr_0',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'sfdr_25',
        value: 'sfdr_25',
        label: [
          'onboarding-questions.sustainability.q_sfdr_percentage.options.sfdr_25',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'sfdr_50',
        value: 'sfdr_50',
        label: [
          'onboarding-questions.sustainability.q_sfdr_percentage.options.sfdr_50',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'sfdr_100',
        value: 'sfdr_100',
        label: [
          'onboarding-questions.sustainability.q_sfdr_percentage.options.sfdr_100',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
  {
    id: 'q_pias_preference',
    block: OnboardingQuestionBlock.Sustainability,
    label: [
      'onboarding-questions.sustainability.q_pias_preference.label',
      { ns: 'components' },
    ],
    types: ['suitability', 'appropriateness'],
    options: [
      {
        id: 'pias_consider',
        value: 'pias_consider',
        label: [
          'onboarding-questions.sustainability.q_pias_preference.options.pias_consider',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'pias_avoid',
        value: 'pias_avoid',
        label: [
          'onboarding-questions.sustainability.q_pias_preference.options.pias_avoid',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
      {
        id: 'pias_exclude',
        value: 'pias_exclude',
        label: [
          'onboarding-questions.sustainability.q_pias_preference.options.pias_exclude',
          { ns: 'components' },
        ],
        score: { suitability: 0, appropriateness: 0 },
      },
    ],
  },
];

export const ONBOARDING_QUESTIONS = new Map([
  [OnboardingQuestionBlock.InvestmentObjectives, INVESTMENT_OBJECTIVES_QUESTIONS],
  [OnboardingQuestionBlock.InvestmentExperience, INVESTMENT_EXPERIENCE_QUESTIONS],
  [OnboardingQuestionBlock.FinancialKnowledge, FINANCIAL_KNOWLEDGE_QUESTIONS],
  [OnboardingQuestionBlock.FinancialSituation, FINANCIAL_SITUATION_QUESTIONS],
  [OnboardingQuestionBlock.Sustainability, SUSTAINABILITY_QUESTIONS],
]) as ReadonlyMap<OnboardingQuestionBlock, OnboardingQuestion[]>;

export const ALL_ONBOARDING_QUESTIONS = new Map([
  ...INVESTMENT_OBJECTIVES_QUESTIONS.map(
    question => [question.id, question] as [string, OnboardingQuestion],
  ),
  ...INVESTMENT_EXPERIENCE_QUESTIONS.map(
    question => [question.id, question] as [string, OnboardingQuestion],
  ),
  ...FINANCIAL_KNOWLEDGE_QUESTIONS.map(
    question => [question.id, question] as [string, OnboardingQuestion],
  ),
  ...FINANCIAL_SITUATION_QUESTIONS.map(
    question => [question.id, question] as [string, OnboardingQuestion],
  ),
  ...SUSTAINABILITY_QUESTIONS.map(
    question => [question.id, question] as [string, OnboardingQuestion],
  ),
]) as ReadonlyMap<string, OnboardingQuestion>;
